<template>
  <span v-if="visible" class="demanda-juris-btn-wrap">
    <button
      type="button"
      class="btn btn-sm btn-warning"
      :disabled="openingModal"
      @click="openModal"
    >
      <i v-if="openingModal" class="fas fa-spinner fa-spin me-1" />
      <i v-else class="fas fa-gavel me-1" />
      {{
        openingModal ? $t("jurisprudencia.loading") : $t("jurisprudencia.searchRelated")
      }}
    </button>

    <div
      v-if="showModal"
      class="modal fade show d-block"
      tabindex="-1"
      style="background: rgba(0, 0, 0, 0.45)"
      @click.self="closeModal"
    >
      <div class="modal-dialog modal-xl modal-dialog-scrollable">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">{{ $t("jurisprudencia.modalTitle") }}</h5>
            <button
              type="button"
              class="btn-close"
              aria-label="Close"
              @click="closeModal"
            />
          </div>
          <div class="modal-body">
            <p v-if="error" class="text-danger">{{ error }}</p>

            <p class="small text-muted mb-2">
              {{ $t("jurisprudencia.escritoJurisModalIntro") }}
            </p>
            <div
              v-if="shortContextHint"
              class="alert alert-info small py-2 mb-2"
              role="status"
            >
              {{ $t("jurisprudencia.escritoJurisShortContextHint") }}
            </div>

            <div
              v-if="!jurisOpeningGreetingReady"
              class="d-flex align-items-center gap-2 small text-secondary py-3"
            >
              <span
                class="spinner-border spinner-border-sm text-warning"
                role="status"
                aria-hidden="true"
              />
              {{ $t("jurisprudencia.escritoJurisGreetingLoading") }}
            </div>
            <template v-else>
              <ConversationalAgentChat
                ref="escritoJurisChat"
                :key="agentChatSessionKey"
                endpoint="/api/jurisprudencia/agent-chat-turn"
                :greeting-variants="jurisAgentGreetingVariants"
                :seed-after-clear="false"
                assistant-bubble-class="bg-warning bg-opacity-75 text-dark border border-warning shadow-sm"
                :placeholder="$t('jurisprudencia.agentChatPlaceholder')"
                :send-label="$t('jurisprudencia.agentChatSend')"
                :clear-label="$t('jurisprudencia.agentChatClear')"
                :loading-label="$t('jurisprudencia.agentChatLoading')"
                :abort-turn-label="$t('jurisprudencia.stopAgentTurn')"
                :request-timeout-ms="240000"
                :request-body-extra="agentChatRequestExtra"
                :allow-abort-while-loading="true"
                :show-patience-hints="true"
                max-height="220px"
                :disabled="false"
                @busy="onAgentChatBusy"
                @turn="onAgentChatTurn"
                @clear="onEscritoAgentClear"
                @error="onAgentChatError"
              />
              <div
                v-if="agentTemaSuggestions.length"
                class="mt-2 pt-2 border-top border-warning border-opacity-25"
              >
                <p class="small text-secondary mb-1">
                  {{ $t("jurisprudencia.agentTemaSuggestIntro") }}
                </p>
                <div class="d-flex flex-wrap gap-1">
                  <button
                    v-for="(sug, si) in agentTemaSuggestions"
                    :key="'sug-' + si + '-' + (sug.text || '').slice(0, 24)"
                    type="button"
                    class="btn btn-outline-secondary btn-sm text-start"
                    :disabled="agentChatBusy"
                    @click="onAgentTemaSuggestionPick(sug)"
                  >
                    {{ sug.text }}
                  </button>
                </div>
              </div>
            </template>

            <template v-if="payload">
              <hr class="my-3" />
              <p class="small text-muted">{{ $t("jurisprudencia.hint") }}</p>
              <p v-if="payload.aiNotas" class="small">
                <strong>{{ $t("jurisprudencia.aiNotes") }}</strong>
                {{ payload.aiNotas }}
              </p>
              <p v-if="payload.queriesUsed && payload.queriesUsed.length" class="small">
                <strong>{{ $t("jurisprudencia.queriesUsed") }}:</strong>
                {{ payload.queriesUsed.join(" · ") }}
              </p>
              <details
                v-if="isAdminUser && payload.saijAdminDebug"
                class="small border rounded p-2 mb-2 bg-warning bg-opacity-10"
              >
                <summary
                  class="fw-semibold user-select-none"
                  style="cursor: pointer; list-style: none"
                >
                  {{ $t("jurisprudencia.adminSaijApiDebugTitle") }}
                </summary>
                <p class="text-muted mb-2 mt-2 small">
                  {{ $t("jurisprudencia.adminSaijApiDebugIntro") }}
                </p>
                <pre
                  class="small mb-0 bg-white border rounded p-2 text-wrap"
                  style="max-height: 16rem; overflow: auto"
                >{{ demandaSaijAdminDebugJson }}</pre>
              </details>
              <div v-if="!payload.results || !payload.results.length" class="text-muted">
                {{ $t("jurisprudencia.noResults") }}
              </div>
              <div v-else>
                <div class="d-flex flex-wrap gap-2 mb-2 small">
                  <button type="button" class="btn btn-link btn-sm p-0" @click="selectAllWithUrl">
                    {{ $t("jurisprudencia.selectAllWithUrl") }}
                  </button>
                  <span class="text-muted">|</span>
                  <button type="button" class="btn btn-link btn-sm p-0" @click="selectNone">
                    {{ $t("jurisprudencia.selectNone") }}
                  </button>
                </div>
                <p class="small text-muted">{{ $t("jurisprudencia.selectRowsHint") }}</p>
                <ul class="list-unstyled">
                  <li
                    v-for="(r, idx) in payload.results"
                    :key="idx"
                    class="mb-3 pb-2 border-bottom d-flex gap-2 align-items-start"
                    :class="{
                      'juris-demanda-row-ai-valid':
                        r && r.relevantToIntent && r.relevanceAnnotated,
                    }"
                  >
                    <div class="form-check mt-1">
                      <input
                        :id="'juris-row-' + idx"
                        v-model="rowSelected[idx]"
                        type="checkbox"
                        class="form-check-input"
                        :disabled="!r.url"
                      />
                    </div>
                    <div class="flex-grow-1">
                      <label
                        class="fw-bold mb-0 d-block"
                        :for="'juris-row-' + idx"
                        style="cursor: pointer"
                      >
                        {{ r.title }}
                        <span
                          v-if="r.relevantToIntent && r.relevanceAnnotated"
                          class="badge bg-success ms-1 align-middle text-white juris-ai-relevant-badge"
                        >
                          {{ $t("jurisprudencia.aiRelevantBadge") }}
                        </span>
                      </label>
                      <div
                        v-if="r.numeroSumario"
                        class="small text-muted mt-1"
                      >
                        {{ $t("jurisprudencia.saijSumarioId") }}:
                        <span class="text-dark">{{ r.numeroSumario }}</span>
                      </div>
                      <div v-if="r.snippet" class="small text-secondary mt-1">
                        {{ truncate(r.snippet, 280) }}
                      </div>
                      <details
                        v-if="r.relevanceJustification"
                        class="mt-1 small juris-why-details"
                      >
                        <summary
                          class="user-select-none fw-semibold"
                          style="cursor: pointer"
                        >
                          {{ $t("jurisprudencia.whyThisResult") }}
                        </summary>
                        <p
                          class="mb-0 mt-2 small juris-why-body"
                          style="white-space: pre-wrap"
                        >
                          {{ r.relevanceJustification }}
                        </p>
                      </details>
                      <div class="small mt-1">
                        <span class="text-muted">{{
                          repairSaijSearchQueryDisplay(r.searchQuery)
                        }}</span>
                        <span v-if="r.url">
                          ·
                          <a :href="r.url" target="_blank" rel="noopener noreferrer">
                            {{ $t("jurisprudencia.openDoc") }}
                          </a>
                        </span>
                        <span v-else class="text-warning">
                          · {{ $t("jurisprudencia.noUrlForRow") }}
                        </span>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </template>
          </div>
          <div class="modal-footer flex-wrap gap-2">
            <button
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="saveDisabled"
              @click="saveReferenciasToDemanda"
            >
              <i v-if="saving" class="fas fa-spinner fa-spin me-1" />
              {{ saving ? $t("jurisprudencia.saving") : $t("jurisprudencia.saveToDemanda") }}
            </button>
            <button type="button" class="btn btn-secondary btn-sm" @click="closeModal">
              {{ $t("Close") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </span>
</template>

<style scoped>
.juris-demanda-row-ai-valid {
  background: rgba(25, 135, 84, 0.14);
  border-radius: 0.35rem;
  padding: 0.5rem 0.35rem 0.65rem !important;
  border-bottom: 2px solid #198754 !important;
  box-shadow: inset 0 0 0 1px rgba(25, 135, 84, 0.3);
}
.juris-ai-relevant-badge {
  font-size: 0.7rem;
  vertical-align: middle;
}
.juris-why-details summary {
  color: #0f5132;
}
.juris-why-body {
  color: #0a3622;
  background: rgba(25, 135, 84, 0.08);
  border-left: 3px solid #198754;
  padding: 0.5rem 0.65rem;
  border-radius: 0.25rem;
}
</style>

<script>
import { wencesApi } from "@/api/wencesApi";
import { ConversationalAgentChat } from "@/components/gotrading/conversational-agent";
import { resolveJurisInfrastructureErrorText } from "@/utils/jurisprudenciaSearchStream";
import Swal from "sweetalert2";
import { useAuthStore } from "@/pages/Auth/stores/auth.store";

export default {
  name: "DemandaJurisprudenciaButton",
  components: { ConversationalAgentChat },
  props: {
    selectedModel: { type: String, default: "" },
    item: { type: Object, default: null },
    canAccessJurisprudencia: { type: Boolean, default: false },
  },
  data() {
    return {
      openingModal: false,
      saving: false,
      showModal: false,
      error: null,
      payload: null,
      rowSelected: {},
      escritoContext: "",
      escritoItemTitle: "",
      shortContextHint: false,
      agentChatSessionKey: 0,
      agentJurisSessionId: null,
      agentChatBusy: false,
      agentTemaSuggestions: [],
      jurisOpeningGreeting: "",
      jurisOpeningGreetingReady: false,
    };
  },
  computed: {
    isAdminUser() {
      return useAuthStore().isAdmin;
    },
    demandaSaijAdminDebugJson() {
      const d = this.payload && this.payload.saijAdminDebug;
      if (d == null) return "";
      try {
        return JSON.stringify(d, null, 2);
      } catch {
        return String(d);
      }
    },
    visible() {
      if (!this.canAccessJurisprudencia) return false;
      if (!this.item || !this.selectedModel) return false;
      return String(this.selectedModel).toLowerCase() === "demanda";
    },
    saveDisabled() {
      if (!this.payload || !this.payload.results || !this.payload.results.length) return true;
      if (!this.itemId) return true;
      if (this.saving) return true;
      const links = this.collectSelectedUrls();
      return links.length === 0;
    },
    itemId() {
      return this.item && this.item._id ? String(this.item._id) : "";
    },
    agentChatRequestExtra() {
      const extra = { jurisHistorySource: "demanda" };
      if (this.escritoContext && String(this.escritoContext).trim()) {
        extra.escritoContext = String(this.escritoContext).trim().slice(0, 16000);
      }
      if (this.escritoItemTitle && String(this.escritoItemTitle).trim()) {
        extra.escritoItemTitle = String(this.escritoItemTitle).trim().slice(0, 500);
      }
      if (this.agentJurisSessionId) {
        extra.jurisAgentSessionId = this.agentJurisSessionId;
      }
      return extra;
    },
    jurisAgentGreetingVariants() {
      const g =
        this.jurisOpeningGreeting != null ? String(this.jurisOpeningGreeting).trim() : "";
      if (g) {
        return [g];
      }
      const out = [];
      for (let i = 1; i <= 8; i += 1) {
        const key = `jurisprudencia.agentChatGreeting${i}`;
        if (this.$te(key)) {
          out.push(this.$t(key));
        }
      }
      return out.length ? out : [this.$t("jurisprudencia.modalTitle")];
    },
  },
  methods: {
    truncate(s, n) {
      const t = s != null ? String(s) : "";
      if (t.length <= n) return t;
      return t.slice(0, n) + "…";
    },
    repairSaijSearchQueryDisplay(raw) {
      const s = raw != null ? String(raw) : "";
      if (!s || (!/Ã/u.test(s) && !/Â/u.test(s))) return s;
      try {
        const bytes = new Uint8Array(s.length);
        for (let i = 0; i < s.length; i += 1) bytes[i] = s.charCodeAt(i) & 0xff;
        const out = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
        if (/\uFFFD/u.test(out)) return s;
        return out;
      } catch {
        return s;
      }
    },
    initRowSelection() {
      const sel = {};
      const results = (this.payload && this.payload.results) || [];
      results.forEach((r, i) => {
        sel[i] = !!(r && r.url);
      });
      this.rowSelected = sel;
    },
    selectAllWithUrl() {
      const results = (this.payload && this.payload.results) || [];
      const sel = { ...this.rowSelected };
      results.forEach((r, i) => {
        if (r && r.url) sel[i] = true;
      });
      this.rowSelected = sel;
    },
    selectNone() {
      const results = (this.payload && this.payload.results) || [];
      const sel = {};
      results.forEach((r, i) => {
        sel[i] = false;
      });
      this.rowSelected = sel;
    },
    collectSelectedUrls() {
      const results = (this.payload && this.payload.results) || [];
      const out = [];
      results.forEach((r, i) => {
        if (this.rowSelected[i] && r && r.url) {
          out.push(String(r.url).trim());
        }
      });
      return out;
    },
    closeModal() {
      this.showModal = false;
      this.openingModal = false;
      this.error = null;
      this.payload = null;
      this.rowSelected = {};
      this.escritoContext = "";
      this.escritoItemTitle = "";
      this.shortContextHint = false;
      this.agentJurisSessionId = null;
      this.agentTemaSuggestions = [];
      this.jurisOpeningGreetingReady = false;
      this.jurisOpeningGreeting = "";
    },
    hasPlantillaRef(plantilla) {
      if (plantilla == null || plantilla === "") return false;
      if (typeof plantilla === "object" && plantilla._id != null) {
        return String(plantilla._id).length > 0;
      }
      return String(plantilla).length > 0;
    },
    plantillaRefId(plantilla) {
      if (!plantilla) return "";
      if (typeof plantilla === "object" && plantilla._id != null) {
        return String(plantilla._id);
      }
      return String(plantilla);
    },
    escritoPlainTextFromRecord(rec) {
      const src = rec || {};
      const keys = ["objeto", "hechos", "derecho", "prueba", "petitorio", "demanda"];
      return keys
        .map((k) => (src[k] != null ? String(src[k]).trim() : ""))
        .filter(Boolean)
        .join("\n\n")
        .trim();
    },
    async fetchEscritoOpeningGreeting() {
      try {
        const { data } = await wencesApi.get(
          "/api/jurisprudencia/agent-opening-greeting",
          { params: { fromEscrito: "1" } }
        );
        if (
          data &&
          data.success &&
          data.greeting &&
          String(data.greeting).trim()
        ) {
          this.jurisOpeningGreeting = String(data.greeting).trim();
          return;
        }
      } catch {
        /* fallback */
      }
      this.jurisOpeningGreeting = this.$t("jurisprudencia.escritoJurisFallbackGreeting");
    },
    onAgentChatBusy(v) {
      this.agentChatBusy = !!v;
    },
    onAgentChatTurn(data) {
      if (!data || data.success === false) return;
      this.agentTemaSuggestions = Array.isArray(data.saijTemaSuggestions)
        ? data.saijTemaSuggestions
        : [];
      if (data.clearJurisAgentSession) {
        this.agentJurisSessionId = null;
      } else if (data.jurisAgentSessionId) {
        this.agentJurisSessionId = data.jurisAgentSessionId;
      }
      if (data.agentSearchAttempted) {
        this.error = null;
        this.payload = {
          success: true,
          aiNotas:
            data.assistantMessage != null
              ? String(data.assistantMessage)
              : data.aiNotas || "",
          queriesUsed: data.queriesUsed || (data.interpreted && data.interpreted.queries) || [],
          results: Array.isArray(data.results) ? data.results : [],
          saijAdminDebug: data.saijAdminDebug || null,
        };
        this.$nextTick(() => this.initRowSelection());
      }
    },
    async onEscritoAgentClear() {
      this.agentJurisSessionId = null;
      this.agentTemaSuggestions = [];
      this.payload = null;
      this.rowSelected = {};
      await this.fetchEscritoOpeningGreeting();
      this.$nextTick(() => {
        this.agentChatSessionKey += 1;
      });
    },
    onAgentChatError(payload) {
      if (payload && payload.userAborted) {
        this.error = this.$t("jurisprudencia.turnStoppedByUser");
        return;
      }
      const message =
        payload && payload.message != null ? String(payload.message) : "";
      this.error = resolveJurisInfrastructureErrorText(
        this,
        message,
        payload && payload.original
      );
    },
    onAgentTemaSuggestionPick(sug) {
      const text = sug && sug.text != null ? String(sug.text).trim() : "";
      if (!text) return;
      const chat = this.$refs.escritoJurisChat;
      if (chat && typeof chat.submitUserMessage === "function") {
        chat.submitUserMessage(`Elijo la sugerencia del tesauro: ${text}`);
      }
    },
    async openModal() {
      this.error = null;
      this.payload = null;
      this.rowSelected = {};
      this.shortContextHint = false;

      let demanda = "";
      let itemName = this.item.name != null ? String(this.item.name) : "";
      let remote = null;
      try {
        const { data } = await wencesApi.get(
          `/api/gotrading/dynamic/${this.selectedModel}/${this.itemId}`
        );
        remote = data;
        if (data) {
          demanda = this.escritoPlainTextFromRecord(data);
        }
        if (data && data.name != null) {
          itemName = String(data.name);
        }
      } catch {
        demanda = this.item ? this.escritoPlainTextFromRecord(this.item) : "";
      }

      const plantillaRef =
        (remote && remote.plantilla) ||
        (this.item && this.item.plantilla) ||
        null;
      const hasPlantilla = this.hasPlantillaRef(plantillaRef);

      let textForSearch = demanda.trim();
      if (!textForSearch && hasPlantilla) {
        const tpvRemote =
          remote && remote.textPlainVersion != null
            ? String(remote.textPlainVersion).trim()
            : "";
        const tpvItem =
          this.item && this.item.textPlainVersion != null
            ? String(this.item.textPlainVersion).trim()
            : "";
        const tpv = tpvRemote || tpvItem;
        if (tpv.length > 0) {
          textForSearch = tpv;
        }
        if (textForSearch.length < 40 && hasPlantilla) {
          const pid = this.plantillaRefId(plantillaRef);
          if (pid) {
            try {
              const { data: plData } = await wencesApi.get(
                `/api/gotrading/dynamic/Plantilla/${pid}`
              );
              const body =
                plData && plData.body != null ? String(plData.body).trim() : "";
              const plPlain =
                plData && plData.textPlainVersion != null
                  ? String(plData.textPlainVersion).trim()
                  : "";
              const extra = [plPlain, body].filter(Boolean).join("\n\n").trim();
              if (extra) {
                textForSearch = [textForSearch, extra]
                  .filter(Boolean)
                  .join("\n\n")
                  .trim();
              }
            } catch {
              /* ignore */
            }
          }
        }
      }

      if (!textForSearch) {
        await Swal.fire({
          icon: "warning",
          title: hasPlantilla
            ? this.$tn("jurisprudencia.emptyDemandPlantillaTitle")
            : this.$tn("jurisprudencia.emptyDemandPopupTitle"),
          text: hasPlantilla
            ? this.$tn("jurisprudencia.emptyDemandPlantillaText")
            : this.$tn("jurisprudencia.emptyDemandPopupText"),
          confirmButtonText: this.$tn("Close"),
        });
        return;
      }

      this.shortContextHint = textForSearch.length < 40;
      this.escritoContext = textForSearch;
      this.escritoItemTitle = itemName;
      this.openingModal = true;
      this.jurisOpeningGreetingReady = false;
      this.jurisOpeningGreeting = "";
      try {
        await this.fetchEscritoOpeningGreeting();
      } finally {
        this.jurisOpeningGreetingReady = true;
        this.openingModal = false;
        this.showModal = true;
        this.agentChatSessionKey += 1;
        this.agentJurisSessionId = null;
        this.agentTemaSuggestions = [];
      }
    },
    async saveReferenciasToDemanda() {
      const links = this.collectSelectedUrls();
      if (!links.length || !this.itemId) return;
      this.saving = true;
      this.error = null;
      try {
        const { data } = await wencesApi.post(
          "/api/jurisprudencia/demanda-referencias",
          {
            itemId: this.itemId,
            links,
          }
        );
        if (!data.success) {
          this.error = data.error || this.$t("jurisprudencia.saveError");
          return;
        }
        this.$root.$emit(
          "show-success",
          this.$t("jurisprudencia.saveOk", { n: data.appendedCount ?? links.length })
        );
        this.$emit("referencias-guardadas", data.referenciasJurisprudencia || []);
        this.closeModal();
      } catch (e) {
        this.error =
          e.response?.data?.error ||
          e.message ||
          this.$t("jurisprudencia.saveError");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.demanda-juris-btn-wrap {
  display: inline-block;
}
</style>
